import { Settings } from '@sendent/nextcloud-api';
import {
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useState,
} from 'react';
import { useAppSelector } from '../store';
import { selectUserSettings } from '../store/selectors/auth';
import { FieldError, FieldErrors } from 'react-hook-form';
import {
    getFirstFormValidationError,
    getFirstValidationError,
} from './validation';
import { Errors, ValidationError } from './model/validation';
import { useDispatch } from 'react-redux';
import { useFilePicker, type FileError } from 'use-file-picker';
import { mapPlainFilesToFileItems } from './file';
import { setFilesToUpload } from '../store/actions/app/actionCreators';
import { selectAppConfig } from '../store/selectors/app';
import { MEGABYTE } from './constants';
import { appApi } from '../services/appApi';
import { LocalStorageHelper, storeKeys } from './storage';
import { collectUnseenReleases, latestRelease } from './release-notes';
import type { ReleaseEntry } from './release-notes';

export const useSettings = (): Settings | null => {
    const userSettings = useAppSelector(selectUserSettings);

    const settings = useMemo(() => {
        if (!userSettings) {
            return null;
        }
        return new Settings(userSettings);
    }, [userSettings]);

    return settings;
};

export const useMediaQuery = (query: string) => {
    const [matches, setMatches] = useState(matchMedia(query).matches);

    useLayoutEffect(() => {
        const mediaQuery = matchMedia(query);
        const listener = () => setMatches(mediaQuery.matches);
        mediaQuery.addEventListener('change', listener);
        return () => mediaQuery.removeEventListener('change', listener);
    }, [query]);

    return matches;
};

export const useUnmount = (destructor: () => void): void => {
    useEffect(() => {
        return destructor;
    });
};

type UseFormValidationDialogResult = {
    showValidationDialog: boolean;
    setShowValidationDialog: React.Dispatch<React.SetStateAction<boolean>>;
    errorToDisplay: FieldError | null;
};

export const useFormValidationDialog = (
    errors: FieldErrors,
): UseFormValidationDialogResult => {
    const [showValidationDialog, setShowValidationDialog] = useState(false);

    const validationError = getFirstFormValidationError(errors);
    const isValid = !Boolean(validationError);

    useEffect(() => {
        if (isValid) {
            return;
        }

        setShowValidationDialog(true);
    }, [validationError]);

    return {
        showValidationDialog,
        setShowValidationDialog,
        errorToDisplay: validationError,
    };
};

type UseCustomValidationDialogResult<T extends string> = {
    showValidationDialog: boolean;
    setShowValidationDialog: React.Dispatch<React.SetStateAction<boolean>>;
    errors: Errors<T>;
    firstError: ValidationError | null;
    setErrors: (errors: Errors<T>) => void;
};

export const useCustomValidationDialog = <
    T extends string = string,
>(): UseCustomValidationDialogResult<T> => {
    const [errors, setErrors] = useState<Errors<T>>({});
    const [showValidationDialog, setShowValidationDialog] = useState(false);

    const setErrorsWrapper = (errors: Errors<T>) => {
        setErrors(errors);
        const hasError = Boolean(getFirstValidationError(errors));

        if (!hasError) {
            return;
        }

        setShowValidationDialog(true);
    };

    return {
        showValidationDialog,
        setShowValidationDialog,
        errors,
        setErrors: setErrorsWrapper,
        firstError: getFirstValidationError(errors),
    };
};

type UseFileProcessorResult = {
    openFileSelector: () => void;
    loading: boolean;
    plainFiles: File[];
    errors: FileError[];
    clear: () => void;
};

type UseReleaseNotesResult = {
    releases: ReleaseEntry[];
    latest: ReleaseEntry | null;
    hasUnseen: boolean;
    markSeen: () => void;
};

const readSeenReleaseVersion = (): string | null =>
    LocalStorageHelper.storeGetObject(storeKeys.RELEASE_NOTES_SEEN);

/**
 * Loads the release notes the user has not seen yet and the latest release,
 * and marks the unseen notes as seen.
 */
export const useReleaseNotes = (): UseReleaseNotesResult => {
    const appVersion = useAppSelector(
        (state) => state.appReducer.appConfig.version,
    );
    const [releases, setReleases] = useState<ReleaseEntry[]>([]);
    const [latest, setLatest] = useState<ReleaseEntry | null>(null);
    const [dismissed, setDismissed] = useState(false);

    useEffect(() => {
        if (!appVersion) return undefined;
        const seenVersion = readSeenReleaseVersion();

        let cancelled = false;
        appApi.getReleaseNotes().then((all) => {
            if (cancelled) return;
            setReleases(collectUnseenReleases(all, seenVersion));
            setLatest(latestRelease(all));
        });
        return () => {
            cancelled = true;
        };
    }, [appVersion]);

    const markSeen = useCallback(() => {
        const newestRelease = releases[0]?.version;
        if (newestRelease) {
            LocalStorageHelper.storeSetObject(
                storeKeys.RELEASE_NOTES_SEEN,
                newestRelease,
            );
        }
        setDismissed(true);
    }, [releases]);

    return {
        releases,
        latest,
        hasUnseen: !dismissed && releases.some((entry) => entry.notable),
        markSeen,
    };
};

export type FileContent = {
    content: ArrayBuffer;
};

export const useFileProcessor = (): UseFileProcessorResult => {
    const dispatch = useDispatch();
    const appConfig = useAppSelector(selectAppConfig);

    const [openFileSelector, { loading, errors, plainFiles, clear }] =
        useFilePicker({
            readFilesContent: false,
            multiple: false,
        });

    useEffect(() => {
        if (loading || plainFiles.length === 0) {
            return;
        }

        const readFiles = async (): Promise<void> => {
            const chunkThreshold =
                appConfig.minChunkedUploadFileSizeMb * MEGABYTE;
            const fileContentArr = await Promise.all<ArrayBuffer>(
                plainFiles.map(async (file) => {
                    if (file.size >= chunkThreshold) {
                        return new ArrayBuffer(0);
                    }

                    const content = await file.arrayBuffer();
                    return content;
                }),
            );

            const newFilesList = mapPlainFilesToFileItems(
                plainFiles,
                fileContentArr,
            ).map((item, index) => {
                const file = plainFiles[index];
                const isSmall = file.size < chunkThreshold;

                if (!isSmall) {
                    return {
                        ...item,
                        content: undefined,
                        readableContent: file,
                        contentlength: file.size,
                    };
                }

                const content = fileContentArr[index];
                return {
                    ...item,
                    content,
                    contentlength: content.byteLength,
                };
            });

            dispatch(setFilesToUpload(newFilesList));
        };

        readFiles();
    }, [clear, dispatch, loading, plainFiles]);

    return {
        openFileSelector,
        plainFiles,
        loading,
        errors,
        clear,
    };
};
